/**
 * Pure state for the AI settings pages, ported from ui-aimanager's
 * `pages/SettingsPage.tsx`: the prompt-config drafts that stay in sync with the
 * server, the token-limit form and the provider-model cost drafts. Everything
 * here is free of React so the sync rules can be tested on their own.
 */
import { z } from 'zod';

import type { AiConfig, ProviderModel, TokenLimitSettings } from '~/modules/query/ai/types';

/** The model a prompt config starts on when the service has not saved one yet. */
export const DEFAULT_MODEL = 'openai-gpt-oss-120b';

// ── Prompt configs ───────────────────────────────────────────────────────────

export type PromptConfigGroup = 'aiWrite' | 'other';

export interface PromptConfigDefinition {
  componentKey: string;
  title: string;
  name: string;
  description: string;
  defaultPromptTemplate: string;
  group: PromptConfigGroup;
}

/**
 * The five application-level prompts. The four placement actions belong to
 * "AI Write"; the prompt-writing assistant is the "Other" section.
 */
export const PROMPT_CONFIG_DEFINITIONS: PromptConfigDefinition[] = [
  {
    componentKey: 'placement_prompt_assist',
    title: 'Prompt writing assistant',
    name: 'Placement Prompt Assist',
    description:
      'Generates and revises placement prompt templates from prompt-assist instructions.',
    defaultPromptTemplate: `You are an expert prompt engineer for BAY placement-based AI experiences.

Generate or revise a single production-ready prompt template using these inputs:
- Instructions: {{instructions}}
- Current prompt template: {{currentPromptTemplate}}
- Context values for relevance: placement={{placementKey}}, component={{componentId}}, version={{componentVersion}}

Rules:
- Return only the final prompt template text.
- Do not include wrapper labels, metadata headers, markdown, code fences, or explanations.
- Never output lines beginning with "PlacementKey:", "ComponentID:", "ComponentVersion:", or "Prompt:".
- Preserve runtime placeholders in double braces when they should remain template variables.
`,
    group: 'other',
  },
  {
    componentKey: 'placement_action_rewrite_instruction',
    title: 'Rewrite action prompt template',
    name: 'Rewrite Action Prompt',
    description: 'Base instruction used by the rewrite action in placement AI assist.',
    defaultPromptTemplate:
      'Rewrite the original input for clarity while preserving meaning and key details.',
    group: 'aiWrite',
  },
  {
    componentKey: 'placement_action_shorten_instruction',
    title: 'Shorten action prompt template',
    name: 'Shorten Action Prompt',
    description: 'Base instruction used by the shorten action in placement AI assist.',
    defaultPromptTemplate:
      'Shorten the original input while preserving its meaning and required details.',
    group: 'aiWrite',
  },
  {
    componentKey: 'placement_action_expand_instruction',
    title: 'Expand action prompt template',
    name: 'Expand Action Prompt',
    description: 'Base instruction used by the expand action in placement AI assist.',
    defaultPromptTemplate:
      'Expand the original input with useful detail while keeping the original intent. Not more than 3 lines.',
    group: 'aiWrite',
  },
  {
    componentKey: 'placement_action_proofread_instruction',
    title: 'Proofread action prompt template',
    name: 'Proofread Action Prompt',
    description: 'Base instruction used by the proofread action in placement AI assist.',
    defaultPromptTemplate:
      'Proofread the original input for grammar, spelling, and punctuation without changing intent.',
    group: 'aiWrite',
  },
];

export function promptDefinitionsForGroup(group: PromptConfigGroup): PromptConfigDefinition[] {
  return PROMPT_CONFIG_DEFINITIONS.filter((definition) => definition.group === group);
}

export interface PromptFormState {
  model: string;
  promptTemplate: string;
}

export function createDefaultPromptForms(): Record<string, PromptFormState> {
  return Object.fromEntries(
    PROMPT_CONFIG_DEFINITIONS.map((definition) => [
      definition.componentKey,
      { model: DEFAULT_MODEL, promptTemplate: definition.defaultPromptTemplate },
    ]),
  );
}

export function createDefaultPromptDirtyState(): Record<string, boolean> {
  return Object.fromEntries(
    PROMPT_CONFIG_DEFINITIONS.map((definition) => [definition.componentKey, false]),
  );
}

/** The server's prompt templates, with the source default standing in for an uninitialized config. */
export function promptServerFormsFromConfigs(
  configs: AiConfig[] | undefined,
): Record<string, PromptFormState> {
  const byComponentKey = new Map((configs ?? []).map((config) => [config.componentKey, config]));
  const forms = createDefaultPromptForms();

  for (const definition of PROMPT_CONFIG_DEFINITIONS) {
    const existing = byComponentKey.get(definition.componentKey);

    if (existing) {
      forms[definition.componentKey] = {
        model: existing.model,
        promptTemplate: existing.promptTemplate,
      };
    }
  }

  return forms;
}

export function arePromptFormStatesEqual(
  left: PromptFormState | undefined,
  right: PromptFormState | undefined,
): boolean {
  if (!(left && right)) {
    return false;
  }

  return left.model === right.model && left.promptTemplate === right.promptTemplate;
}

/**
 * The editable prompt state. `server` is the last value the service returned;
 * `drafts` may hold an edit the person has not saved. A refetch replaces a clean
 * draft, keeps a dirty one, and adopts the server value once the edit matches it
 * (which is what a successful save produces).
 */
export interface PromptFormsState {
  server: Record<string, PromptFormState>;
  drafts: Record<string, PromptFormState>;
  dirty: Record<string, boolean>;
  hydrated: boolean;
}

export function createPromptFormsState(): PromptFormsState {
  const defaults = createDefaultPromptForms();

  return {
    server: defaults,
    drafts: defaults,
    dirty: createDefaultPromptDirtyState(),
    hydrated: false,
  };
}

export function syncPromptForms(
  state: PromptFormsState,
  server: Record<string, PromptFormState>,
): PromptFormsState {
  if (!state.hydrated) {
    return { server, drafts: server, dirty: createDefaultPromptDirtyState(), hydrated: true };
  }

  let changed = false;
  const drafts = { ...state.drafts };
  const dirty = { ...state.dirty };

  for (const definition of PROMPT_CONFIG_DEFINITIONS) {
    const componentKey = definition.componentKey;
    const incoming = server[componentKey] ?? state.server[componentKey];
    const previousDraft = state.drafts[componentKey];
    const previousServer = state.server[componentKey];
    const wasDirty = Boolean(state.dirty[componentKey]);

    let nextDraft = incoming;
    let nextDirty = false;

    if (wasDirty && previousDraft) {
      const draftDiffersFromPreviousServer = previousServer
        ? !arePromptFormStatesEqual(previousDraft, previousServer)
        : true;
      const draftAlreadyMatchesIncoming = arePromptFormStatesEqual(previousDraft, incoming);

      if (draftDiffersFromPreviousServer && !draftAlreadyMatchesIncoming) {
        // The person is mid-edit: an unrelated refetch must not overwrite it.
        nextDraft = previousDraft;
        nextDirty = true;
      }
    }

    if (
      !arePromptFormStatesEqual(previousDraft, nextDraft) ||
      Boolean(state.dirty[componentKey]) !== nextDirty
    ) {
      changed = true;
    }

    drafts[componentKey] = nextDraft;
    dirty[componentKey] = nextDirty;
  }

  if (!changed) {
    return state;
  }

  return { server, drafts, dirty, hydrated: true };
}

/** Applies a field edit and recomputes the dirty flag against the last server value. */
export function editPromptForm(
  state: PromptFormsState,
  componentKey: string,
  patch: Partial<PromptFormState>,
): PromptFormsState {
  const base = state.drafts[componentKey] ?? state.server[componentKey];

  if (!base) {
    return state;
  }

  const draft = { ...base, ...patch };
  const server = state.server[componentKey];

  return {
    ...state,
    drafts: { ...state.drafts, [componentKey]: draft },
    dirty: {
      ...state.dirty,
      [componentKey]: server ? !arePromptFormStatesEqual(draft, server) : true,
    },
  };
}

export function resetPromptForm(state: PromptFormsState, componentKey: string): PromptFormsState {
  const server = state.server[componentKey];

  if (!server) {
    return state;
  }

  return {
    ...state,
    drafts: { ...state.drafts, [componentKey]: server },
    dirty: { ...state.dirty, [componentKey]: false },
  };
}

/** Records a saved value (a successful create or update) as both server and draft. */
export function commitPromptForm(
  state: PromptFormsState,
  componentKey: string,
  value: PromptFormState,
): PromptFormsState {
  return {
    ...state,
    server: { ...state.server, [componentKey]: value },
    drafts: { ...state.drafts, [componentKey]: value },
    dirty: { ...state.dirty, [componentKey]: false },
  };
}

// ── Token limits ─────────────────────────────────────────────────────────────

export interface TokenLimitsFormState {
  dailyUserLimit: string;
  monthlyOrgLimit: string;
}

export const TOKEN_LIMIT_POSITIVE_INTEGER_ERROR =
  'Enter a whole number of tokens greater than zero.';

export function createDefaultTokenLimitsFormState(): TokenLimitsFormState {
  return { dailyUserLimit: '10000', monthlyOrgLimit: '1000000' };
}

export function tokenLimitsFormFromLimits(
  limits: TokenLimitSettings | undefined | null,
): TokenLimitsFormState {
  if (!limits) {
    return createDefaultTokenLimitsFormState();
  }

  return {
    dailyUserLimit: String(limits.dailyUserLimit),
    monthlyOrgLimit: String(limits.monthlyOrgLimit),
  };
}

/** Strict: a trimmed run of digits above zero. "1.5", "-1" and "12abc" are refused, not truncated. */
export function parsePositiveIntegerInput(value: string): number | null {
  const trimmed = value.trim();

  if (!/^\d+$/.test(trimmed)) {
    return null;
  }

  const parsed = Number.parseInt(trimmed, 10);

  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
}

const positiveInteger = z
  .string()
  .refine((value) => parsePositiveIntegerInput(value) !== null, TOKEN_LIMIT_POSITIVE_INTEGER_ERROR);

export const tokenLimitsSchema = z.object({
  dailyUserLimit: positiveInteger,
  monthlyOrgLimit: positiveInteger,
});

export function tokenLimitsFormToSettings(form: TokenLimitsFormState): TokenLimitSettings | null {
  const dailyUserLimit = parsePositiveIntegerInput(form.dailyUserLimit);
  const monthlyOrgLimit = parsePositiveIntegerInput(form.monthlyOrgLimit);

  if (dailyUserLimit === null || monthlyOrgLimit === null) {
    return null;
  }

  return { dailyUserLimit, monthlyOrgLimit };
}

export function hasTokenLimitsChanges(
  form: TokenLimitsFormState,
  limits: TokenLimitSettings | undefined | null,
): boolean {
  if (!limits) {
    return false;
  }

  return (
    form.dailyUserLimit.trim() !== String(limits.dailyUserLimit) ||
    form.monthlyOrgLimit.trim() !== String(limits.monthlyOrgLimit)
  );
}

// ── Provider model costs ─────────────────────────────────────────────────────

export type ProviderCostField = 'inputCostPer1M' | 'outputCostPer1M';

export interface ProviderCostDraft {
  inputCostPerMillion: string;
  outputCostPerMillion: string;
}

export function defaultProviderCostDraft(): ProviderCostDraft {
  return { inputCostPerMillion: '', outputCostPerMillion: '' };
}

export function providerCostDirtyKey(modelId: string, field: ProviderCostField): string {
  return `${modelId}:${field}`;
}

export function formatProviderCost(value: number | null): string {
  return value === null ? '' : String(value);
}

/** Input characters a cost field accepts while typing; "" clears the price. */
export function isProviderCostInput(value: string): boolean {
  return /^\d*\.?\d*$/.test(value);
}

export type ProviderCostParseResult = { ok: true; value: number | null } | { ok: false };

export function parseProviderCostInput(value: string): ProviderCostParseResult {
  const trimmed = value.trim();

  if (trimmed === '') {
    return { ok: true, value: null };
  }

  const parsed = Number(trimmed);

  if (!Number.isFinite(parsed) || parsed < 0) {
    return { ok: false };
  }

  return { ok: true, value: parsed };
}

export function providerCostDraftValue(
  draft: ProviderCostDraft | undefined,
  field: ProviderCostField,
): string {
  if (!draft) {
    return '';
  }

  return field === 'inputCostPer1M' ? draft.inputCostPerMillion : draft.outputCostPerMillion;
}

/**
 * Rebuilds the cost drafts from the provider catalog. A field the person is
 * still editing (`dirtyKeys`) keeps its typed value; every other field follows
 * the server, so a saved price lands without a reload.
 */
export function syncProviderCostDrafts(
  previous: Record<string, ProviderCostDraft>,
  dirtyKeys: ReadonlySet<string>,
  models: ProviderModel[],
): Record<string, ProviderCostDraft> {
  const next: Record<string, ProviderCostDraft> = {};

  for (const model of models) {
    const existing = previous[model.id];
    const inputDirty = dirtyKeys.has(providerCostDirtyKey(model.id, 'inputCostPer1M'));
    const outputDirty = dirtyKeys.has(providerCostDirtyKey(model.id, 'outputCostPer1M'));

    next[model.id] = {
      inputCostPerMillion: inputDirty
        ? (existing?.inputCostPerMillion ?? formatProviderCost(model.inputCostPer1M))
        : formatProviderCost(model.inputCostPer1M),
      outputCostPerMillion: outputDirty
        ? (existing?.outputCostPerMillion ?? formatProviderCost(model.outputCostPer1M))
        : formatProviderCost(model.outputCostPer1M),
    };
  }

  return next;
}

// ── Provider model in use ────────────────────────────────────────────────────

export interface ProviderModelUsageRef {
  id: string;
  name: string | null;
  assistantKey?: string;
  placementKey?: string;
  componentKey?: string;
}

export interface ProviderModelInUse {
  configs: ProviderModelUsageRef[];
  digitalAssistants: ProviderModelUsageRef[];
  placements: ProviderModelUsageRef[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function toUsageRefList(value: unknown): ProviderModelUsageRef[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(isRecord).map((entry) => ({
    id: typeof entry.id === 'string' ? entry.id : '',
    name: typeof entry.name === 'string' ? entry.name : null,
    assistantKey: typeof entry.assistantKey === 'string' ? entry.assistantKey : undefined,
    placementKey: typeof entry.placementKey === 'string' ? entry.placementKey : undefined,
    componentKey: typeof entry.componentKey === 'string' ? entry.componentKey : undefined,
  }));
}

/** The dependents service-ai names when it refuses to deactivate a model. */
export function extractProviderModelInUse(error: unknown): ProviderModelInUse | null {
  if (!isRecord(error) || !isRecord(error.data) || error.data.code !== 'PROVIDER_MODEL_IN_USE') {
    return null;
  }

  return {
    configs: toUsageRefList(error.data.usedByConfigs),
    digitalAssistants: toUsageRefList(error.data.usedByDigitalAssistants),
    placements: toUsageRefList(error.data.usedByPlacements),
  };
}

/** What the in-use modal lists for a dependent: its name, else its natural key. */
export function providerUsageRefLabel(ref: ProviderModelUsageRef): string {
  return ref.name || ref.assistantKey || ref.placementKey || ref.componentKey || ref.id;
}
